import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, Plus, Trash2, ChevronDown, ChevronRight, Link as LinkIcon,
  CheckCircle2, Circle, ExternalLink, LayoutTemplate, Pencil, X, Check,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function Onboarding() {
  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <UserPlus className="h-3 w-3" /> People · On-boarding
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">On-boarding</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track new-starter schedules and manage the reusable templates that pre-populate them.
        </p>
      </div>

      <Tabs defaultValue="starters">
        <TabsList>
          <TabsTrigger value="starters" data-testid="ob-tab-starters">Starters</TabsTrigger>
          <TabsTrigger value="templates" data-testid="ob-tab-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="starters" className="mt-6"><StartersPanel /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplatesPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// STARTERS
// -----------------------------------------------------------------------------
function StartersPanel() {
  const [starters, setStarters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ starter_name: "", role: "", start_date: "", manager_id: "", template_id: "" });
  const [expanded, setExpanded] = useState({});

  const reload = async () => {
    const [s, t, u] = await Promise.all([
      api.get("/onboarding/starters"), api.get("/onboarding/templates"), api.get("/users"),
    ]);
    setStarters(s.data); setTemplates(t.data); setUsers(u.data);
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.starter_name.trim()) return toast.error("Starter name required");
    try {
      await api.post("/onboarding/starters", {
        starter_name: form.starter_name.trim(),
        role: form.role || null,
        start_date: form.start_date || null,
        manager_id: form.manager_id || null,
        template_id: form.template_id || null,
      });
      setForm({ starter_name: "", role: "", start_date: "", manager_id: "", template_id: "" });
      setCreating(false);
      await reload();
      toast.success("Starter added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeStarter = async (id) => {
    if (!window.confirm("Delete this starter's on-boarding?")) return;
    try { await api.delete(`/onboarding/starters/${id}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div className="text-xs text-slate-500">{starters.length} starter{starters.length === 1 ? "" : "s"}</div>
        <Button onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-starter">
          <Plus className="h-4 w-4 mr-1" /> New starter
        </Button>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add a new starter</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Name *</label>
              <Input data-testid="starter-name" value={form.starter_name} onChange={(e) => setForm({ ...form, starter_name: e.target.value })} placeholder="e.g. Jane Doe" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Role</label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. SDR" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Start date</label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <UserPick label="Manager" users={users} value={form.manager_id} onChange={(v) => setForm({ ...form, manager_id: v })} />
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Pre-populate from template</label>
              <Select value={form.template_id || "none"} onValueChange={(v) => setForm({ ...form, template_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="starter-template"><SelectValue placeholder="No template — blank schedule" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template — blank schedule</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="save-starter">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {starters.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">
          No starters yet. Click <b>New starter</b> to begin an onboarding.
        </CardContent></Card>
      ) : starters.map((s) => {
        const open = !!expanded[s.id];
        const totalTasks = (s.sections || []).reduce((n, sec) => n + (sec.tasks || []).length, 0);
        const doneTasks = (s.sections || []).reduce((n, sec) => n + (sec.tasks || []).filter((t) => t.status === "completed").length, 0);
        return (
          <Card key={s.id} className="border-slate-200" data-testid={`starter-card-${s.id}`}>
            <CardHeader className="border-b border-slate-100 py-3">
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => setExpanded((st) => ({ ...st, [s.id]: !open }))} className="flex items-center gap-2 text-left flex-1">
                  {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                  <CardTitle className="text-base font-heading">{s.starter_name}</CardTitle>
                  {s.role && <Badge variant="outline" className="text-[10px]">{s.role}</Badge>}
                  {s.start_date && <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-700">Start {fmtDate(s.start_date)}</Badge>}
                  {s.manager_name && <Badge variant="outline" className="text-[10px]">Manager: {s.manager_name}</Badge>}
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">{doneTasks}/{totalTasks} tasks</Badge>
                </button>
                <Button variant="ghost" size="icon" onClick={() => removeStarter(s.id)} data-testid={`delete-starter-${s.id}`}>
                  <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                </Button>
              </div>
            </CardHeader>
            {open && <CardContent className="p-5"><StarterBody starter={s} users={users} onChange={reload} /></CardContent>}
          </Card>
        );
      })}
    </div>
  );
}

function StarterBody({ starter, users, onChange }) {
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");

  const addSection = async () => {
    if (!newSectionTitle.trim()) return;
    try {
      await api.post(`/onboarding/starters/${starter.id}/sections`, { title: newSectionTitle.trim() });
      setNewSectionTitle(""); setAddingSection(false);
      await onChange();
    } catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      {(starter.sections || []).map((sec) => (
        <SectionBlock key={sec.id} starterId={starter.id} section={sec} users={users} onChange={onChange} />
      ))}

      {addingSection ? (
        <div className="flex gap-2 items-center bg-slate-50 border border-dashed border-slate-300 rounded p-3">
          <Input autoFocus placeholder="New section title" value={newSectionTitle} onChange={(e) => setNewSectionTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSection()} />
          <Button size="sm" onClick={addSection}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAddingSection(false); setNewSectionTitle(""); }}>Cancel</Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAddingSection(true)} data-testid={`add-section-${starter.id}`}>
          <Plus className="h-3 w-3 mr-1" /> Add section
        </Button>
      )}
    </div>
  );
}

function SectionBlock({ starterId, section, users, onChange }) {
  const [addingTask, setAddingTask] = useState(false);
  const [taskDraft, setTaskDraft] = useState({ action: "", notes: "", owner_id: "", links: [] });

  const addTask = async () => {
    if (!taskDraft.action.trim()) return toast.error("Action required");
    try {
      await api.post(`/onboarding/starters/${starterId}/sections/${section.id}/tasks`, {
        action: taskDraft.action.trim(),
        notes: taskDraft.notes.trim() || null,
        owner_id: taskDraft.owner_id || null,
        links: taskDraft.links.filter((l) => l.url.trim()),
      });
      setTaskDraft({ action: "", notes: "", owner_id: "", links: [] });
      setAddingTask(false);
      await onChange();
    } catch (_) { toast.error("Failed"); }
  };

  const removeSection = async () => {
    if (!window.confirm(`Delete section "${section.title}" and all its tasks?`)) return;
    try { await api.delete(`/onboarding/starters/${starterId}/sections/${section.id}`); await onChange(); }
    catch (_) { toast.error("Failed"); }
  };

  const done = (section.tasks || []).filter((t) => t.status === "completed").length;
  const total = (section.tasks || []).length;

  return (
    <div className="rounded border border-slate-200" data-testid={`section-${section.id}`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 bg-slate-50">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm text-slate-800">{section.title}</div>
          <Badge variant="outline" className="text-[10px]">{done}/{total}</Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={removeSection}>
          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
        </Button>
      </div>

      <div className="p-3 space-y-2">
        {(section.tasks || []).length === 0 && !addingTask && (
          <div className="text-xs text-slate-400 py-2 text-center">No tasks yet.</div>
        )}
        {(section.tasks || []).map((t) => (
          <TaskRow key={t.id} starterId={starterId} task={t} users={users} onChange={onChange} />
        ))}

        {addingTask ? (
          <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3 space-y-2">
            <Input autoFocus placeholder="Action *" value={taskDraft.action} onChange={(e) => setTaskDraft({ ...taskDraft, action: e.target.value })} data-testid={`new-task-action-${section.id}`} />
            <Textarea rows={2} placeholder="Notes (optional)" value={taskDraft.notes} onChange={(e) => setTaskDraft({ ...taskDraft, notes: e.target.value })} />
            <UserPick label="Owner" compact users={users} value={taskDraft.owner_id} onChange={(v) => setTaskDraft({ ...taskDraft, owner_id: v })} />
            <LinksEditor links={taskDraft.links} onChange={(links) => setTaskDraft({ ...taskDraft, links })} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setAddingTask(false); setTaskDraft({ action: "", notes: "", owner_id: "", links: [] }); }}>Cancel</Button>
              <Button size="sm" onClick={addTask} className="bg-blue-600 hover:bg-blue-700" data-testid={`save-task-${section.id}`}>Add task</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAddingTask(true)} data-testid={`add-task-${section.id}`}>
            <Plus className="h-3 w-3 mr-1" /> Add task
          </Button>
        )}
      </div>
    </div>
  );
}

function TaskRow({ starterId, task, users, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const toggle = async () => {
    try {
      await api.patch(`/onboarding/starters/${starterId}/tasks/${task.id}`, {
        status: task.status === "completed" ? "not_complete" : "completed",
      });
      await onChange();
    } catch (_) { toast.error("Failed"); }
  };

  const removeTask = async () => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/onboarding/starters/${starterId}/tasks/${task.id}`); await onChange(); }
    catch (_) { toast.error("Failed"); }
  };

  const startEdit = () => {
    setDraft({
      action: task.action || "",
      notes: task.notes || "",
      owner_id: task.owner_id || "",
      links: [...(task.links || [])],
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/onboarding/starters/${starterId}/tasks/${task.id}`, {
        action: draft.action,
        notes: draft.notes,
        owner_id: draft.owner_id || null,
        links: draft.links.filter((l) => l.url.trim()),
      });
      setEditing(false);
      await onChange();
    } catch (_) { toast.error("Failed"); }
  };

  const done = task.status === "completed";

  if (editing) {
    return (
      <div className="rounded border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid={`task-edit-${task.id}`}>
        <Input value={draft.action} onChange={(e) => setDraft({ ...draft, action: e.target.value })} />
        <Textarea rows={2} placeholder="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
        <UserPick label="Owner" compact users={users} value={draft.owner_id} onChange={(v) => setDraft({ ...draft, owner_id: v })} />
        <LinksEditor links={draft.links} onChange={(links) => setDraft({ ...draft, links })} />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="h-3 w-3 mr-1" />Cancel</Button>
          <Button size="sm" onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700"><Check className="h-3 w-3 mr-1" />Save</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-slate-200 p-3 flex items-start gap-3" data-testid={`task-row-${task.id}`}>
      <button onClick={toggle} className="mt-0.5" data-testid={`task-toggle-${task.id}`}>
        {done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300 hover:text-slate-500" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${done ? "text-slate-400 line-through" : "text-slate-900"}`}>{task.action}</div>
        {task.notes && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{task.notes}</div>}
        <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
          {task.owner_name && <Badge variant="outline" className="text-[10px]">{task.owner_name}</Badge>}
          {done && task.completed_at && <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">Completed {fmtDate(task.completed_at)}</Badge>}
          {(task.links || []).map((l, i) => (
            <a key={i} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] bg-blue-50 border border-blue-200 text-blue-700 rounded px-1.5 py-0.5 hover:bg-blue-100">
              <ExternalLink className="h-2.5 w-2.5" /> {l.label || l.url}
            </a>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="ghost" size="icon" onClick={startEdit} data-testid={`task-edit-btn-${task.id}`}>
          <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
        </Button>
        <Button variant="ghost" size="icon" onClick={removeTask}>
          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TEMPLATES
// -----------------------------------------------------------------------------
function TemplatesPanel() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);

  const reload = async () => {
    const t = await api.get("/onboarding/templates");
    setTemplates(t.data);
  };
  useEffect(() => { reload(); }, []);

  const removeTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/onboarding/templates/${id}`); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{templates.length} template{templates.length === 1 ? "" : "s"}</div>
        <Button onClick={() => navigate("/app/onboarding/templates/new")} className="bg-blue-600 hover:bg-blue-700" data-testid="new-template">
          <Plus className="h-4 w-4 mr-1" /> New template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">
          No templates yet. Create one so future starters get pre-populated.
        </CardContent></Card>
      ) : templates.map((t) => {
        const totalTasks = (t.sections || []).reduce((n, s) => n + (s.tasks || []).length, 0);
        return (
          <Card key={t.id} className="border-slate-200 hover:border-blue-300 transition cursor-pointer" data-testid={`template-card-${t.id}`}
            onClick={() => navigate(`/app/onboarding/templates/${t.id}/edit`)}>
            <CardHeader className="border-b border-slate-100 py-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <LayoutTemplate className="h-4 w-4 text-blue-600" /> {t.name}
                  </CardTitle>
                  {t.description && <div className="text-xs text-slate-500 mt-1">{t.description}</div>}
                  <div className="flex gap-1.5 mt-2">
                    <Badge variant="outline" className="text-[10px]">{(t.sections || []).length} sections</Badge>
                    <Badge variant="outline" className="text-[10px]">{totalTasks} tasks</Badge>
                  </div>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/app/onboarding/templates/${t.id}/edit`)}>
                    <Pencil className="h-4 w-4 mr-1 text-slate-500" /> Edit
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeTemplate(t.id)}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(t.sections || []).map((sec) => (
                  <div key={sec.id} className="rounded border border-slate-200 p-2">
                    <div className="font-medium text-sm text-slate-900">{sec.title}</div>
                    <ul className="text-xs text-slate-600 mt-1 space-y-0.5 list-disc list-inside">
                      {(sec.tasks || []).slice(0, 5).map((t) => <li key={t.id} className="truncate">{t.action}</li>)}
                      {(sec.tasks || []).length > 5 && <li className="text-slate-400">+{(sec.tasks || []).length - 5} more</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------------------
function UserPick({ label, users, value, onChange, compact = false }) {
  return (
    <div>
      {!compact && <label className="text-[10px] font-mono uppercase text-slate-500">{label}</label>}
      <Select value={value || "unassigned"} onValueChange={(v) => onChange(v === "unassigned" ? "" : v)}>
        <SelectTrigger><SelectValue placeholder={compact ? label : "Unassigned"} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="unassigned">{compact ? `${label} — unassigned` : "Unassigned"}</SelectItem>
          {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LinksEditor({ links, onChange }) {
  const add = () => onChange([...(links || []), { label: "", url: "" }]);
  const update = (i, patch) => onChange(links.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1 mb-1"><LinkIcon className="h-3 w-3" /> Links</div>
      <div className="space-y-1.5">
        {(links || []).map((l, i) => (
          <div key={i} className="flex gap-1.5 items-center">
            <Input className="w-28 text-xs" placeholder="Label" value={l.label} onChange={(e) => update(i, { label: e.target.value })} />
            <Input className="flex-1 text-xs" placeholder="https://…" value={l.url} onChange={(e) => update(i, { url: e.target.value })} />
            <Button size="icon" variant="ghost" onClick={() => remove(i)}><X className="h-3 w-3 text-slate-400" /></Button>
          </div>
        ))}
        <Button size="sm" variant="ghost" onClick={add}><Plus className="h-3 w-3 mr-1" />Add link</Button>
      </div>
    </div>
  );
}
